import { AxiosError } from 'axios'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { getErrorResponseData } from '@app/routing/Session/ErrorResponse'

import { Form } from '@lib/formik'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'

import { ResponseError } from '@utils/ApiClient'
import { useWindowResize } from '@utils/domUtils'
import { FormSubmitValues } from '@utils/formValidation'

import { usePasswordValidation } from '@global/PasswordValidation/PasswordValidation'
import { createFormPasswordField } from '@global/formField/FormPasswordField'

import { useOnLocaleSwitch } from '@hooks/useOnLocaleSwitch'

import { Box } from '@shared/components/Box'

export interface ChangePasswordFormValues {
  currentPassword: string
  newPassword: string
}

const FormField = createFormPasswordField<ChangePasswordFormValues>()

const ChangePasswordFormUI: React.FC<FormikProps<ChangePasswordFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, setSubmitting, dirty, values, isValid, isSubmitting } = props
  const isMobile = useWindowResize()
  const { t } = useTranslation()
  useOnLocaleSwitch(() => props.validateForm())

  const { isPasswordValid, validationComponent } = usePasswordValidation({
    password: values.newPassword,
  })

  const handleSubmitForm = (event: SyntheticEvent) => {
    event.preventDefault()
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <Form onSubmit={handleSubmitForm}>
      <Box flex align='flex-start' direction='column' gap={isMobile ? 'x24' : 'x32'} maxWidth={500}>
        <Box flex direction='column' gap='x16'>
          <Heading6 semibold>{t('Profile.Change_Password')}</Heading6>
          <Body1>{t('Profile.Change_password_strong')}</Body1>
        </Box>

        <Box flex direction='column' gap='x16' fullWidth maxWidth={400}>
          <FormField name='currentPassword' type={'password'} label={t('Old Password')} />
          <FormField name='newPassword' type={'password'} label={t('New Password')} />
        </Box>
        {validationComponent}

        <Button.Primary
          type='submit'
          size='lg'
          loading={isSubmitting}
          fullWidth={isMobile}
          disabled={!(isPasswordValid && isValid && dirty && !isSubmitting)}
        >
          {t('Profile.Change_Password')}
        </Button.Primary>
      </Box>
    </Form>
  )
}

interface OuterProps {
  onSubmit(values: FormSubmitValues<ChangePasswordFormValues>): Promise<void>
}

export const ChangePasswordForm = withFormik<OuterProps, ChangePasswordFormValues>({
  mapPropsToValues: () => {
    return {
      currentPassword: '',
      newPassword: '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting, setErrors }) => {
    try {
      await props.onSubmit(values)
    } catch (error: unknown) {
      if (error instanceof AxiosError || error instanceof ResponseError) {
        const { errorCode } = getErrorResponseData(error as ResponseError)
        if (errorCode === 'invalid_credentials') {
          setErrors({
            currentPassword: t('errors.Password does not match'),
          })
        }
      }
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<ChangePasswordFormValues> = {}
    if (!values.currentPassword) {
      errors.currentPassword = t('Validation.Required')
    }

    return errors
  },
  enableReinitialize: true,
  isInitialValid: false,
})(ChangePasswordFormUI)
